import { useCallback, useMemo } from 'react'
import {
  useApolloClient,
  useQuery,
  ApolloClient,
  NormalizedCacheObject,
  OperationVariables,
  gql,
  ApolloError,
  ApolloQueryResult,
} from '@apollo/client'
import { emitGraphQLError } from './errors'
import { refetchGraphQLResourceFetch } from './service'
import { buildGraphQLQuery, useDeepCompareMemo } from './utils'
import type {
  UseGraphQLResourceFetchProps,
  GraphQLResourceFetchResult,
  GraphQLResourceFetchOptions,
} from './types'

/**
 * React hook for executing resource-based GraphQL queries with built-in state management.
 * Now uses Apollo's useQuery directly for normalized cache benefits while maintaining
 * the convenient resource-based API. Components will automatically see updates when
 * mutations modify the same resource.
 *
 * @param props - Hook configuration including resource name, object UID, fields, etc.
 * @returns Query result with data, loading state, error, and refetch function
 */
export const useGraphQLResourceFetch = <T = unknown>({
  resourceName,
  objectUid,
  fragment,
  fields,
  skip = false,
  fetchPolicy = 'cache-first',
  variables = {},
}: UseGraphQLResourceFetchProps): GraphQLResourceFetchResult<T> => {
  // Get Apollo client at the top level
  const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>

  // Stabilize variables with deep comparison to avoid unnecessary re-renders
  const stableVariables = useDeepCompareMemo(variables)

  // Build the GraphQL query from resource parameters
  const queryInfo = useMemo(() => {
    if (skip || !objectUid) return null

    try {
      if (fragment && typeof fragment !== 'string') {
        throw new Error('useGraphQLResourceFetch: fragment must be a string')
      }
      // Ensure UID is always included for cache normalization
      const fieldsWithUID =
        fields && !fields.includes('UID')
          ? ['UID', ...fields]
          : fields || ['UID']

      return buildGraphQLQuery({
        resourceName,
        objectUid,
        fragment,
        fields: fieldsWithUID,
        variables: stableVariables,
      })
    } catch (error) {
      console.error('Failed to build GraphQL query:', error)
      return null
    }
  }, [
    skip,
    objectUid,
    resourceName,
    fragment,
    fields,
    stableVariables, // Deep comparison for stable object reference
  ])

  // Use Apollo's useQuery hook for optimal state management (same pattern as useGraphQLFetch)
  const {
    data: apolloData,
    loading,
    error,
    refetch: apolloRefetch,
  } = useQuery<Record<string, T>>(
    queryInfo?.query ||
      gql`
        query EmptyQuery {
          __typename
        }
      `,
    {
      variables: queryInfo?.finalVariables || {},
      skip: skip || !objectUid || !queryInfo || !queryInfo?.query,
      fetchPolicy,
      errorPolicy: 'all', // Return both data and errors
      notifyOnNetworkStatusChange: true,
    },
  )

  // Extract the resource data from Apollo's response
  const data = useMemo((): T | null => {
    if (!apolloData || !queryInfo) return null
    return apolloData[queryInfo.queryName] || null
  }, [apolloData, queryInfo])

  // Memoize query options for the custom refetch function
  const queryOptions = useMemo((): GraphQLResourceFetchOptions | null => {
    if (skip || !objectUid) return null

    return {
      resourceName,
      objectUid,
      fragment,
      fields,
      fetchPolicy,
      variables: stableVariables,
    }
  }, [
    skip,
    objectUid,
    resourceName,
    fragment,
    fields,
    fetchPolicy,
    stableVariables, // Deep comparison for stable object reference
  ])

  /**
   * Enhanced refetch function that works with our service layer.
   * Provides additional flexibility for variable overrides.
   */
  const refetch = useCallback(
    async (overrideVariables?: OperationVariables) => {
      if (!queryOptions) return

      // If no override variables, use Apollo's built-in refetch
      if (!overrideVariables || Object.keys(overrideVariables).length === 0) {
        const result = await apolloRefetch()
        return result as ApolloQueryResult<T>
      }

      // For variable overrides, use our service layer
      try {
        const result = await refetchGraphQLResourceFetch<T>(
          apolloClient,
          queryOptions,
          overrideVariables,
        )
        return result
      } catch (err) {
        throw err as ApolloError
      }
    },
    [queryOptions, apolloRefetch, apolloClient],
  )

  return {
    data: data || null,
    loading,
    error: (() => {
      if (error)
        emitGraphQLError(error, {
          operation: 'query',
          queryName: queryInfo?.queryName,
          resourceName,
          objectUid,
        })
      return error || null
    })(),
    refetch,
  }
}
